import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { mainSiteUrl } from '@/mocks/categories';

export default function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">{t('nav.categories')}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Product Categories
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Browse all product categories available through the AllChinaBuy spreadsheet. From shoes and clothing to accessories and perfumes, find exactly what you are looking for and click through to explore full product listings on the main website.
          </p>
        </div>
      </section>

      {/* SEO Content */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>How AllChinaBuy Categories Are Organized</h2>
            <p>
              The AllChinaBuy spreadsheet organizes products into clear, shopper-friendly categories that make browsing and discovering items straightforward. Whether you are looking for the latest sneaker releases, cozy hoodies, trendy accessories, or everyday essentials, each category contains curated listings with pricing, images, and direct purchase links.
            </p>
            <p>
              Categories are updated regularly as new products arrive and seasonal trends change. The spreadsheet structure ensures that you can quickly navigate between sections without losing track of items you have already browsed. This categorical approach is one of the reasons the AllChinaBuy spreadsheet has become a popular resource for buyers who want an organized overview before diving into individual product pages.
            </p>
            <h2>Popular Categories in 2026</h2>
            <p>
              <strong>Shoes</strong> remains the most visited category, featuring everything from casual sneakers to high-performance athletic footwear. Prices are listed in both Chinese Yuan and US Dollars, making it easy to compare costs regardless of your preferred currency. <strong>Hoodies and Sweaters</strong> follow closely, with seasonal collections that rotate throughout the year. <strong>T-Shirts</strong>, <strong>Jackets</strong>, and <strong>Pants/Shorts</strong> round out the clothing section with diverse styles for different tastes and occasions.
            </p>
            <p>
              For accessories, the <strong>Headwear</strong>, <strong>Bags</strong>, <strong>Belts</strong>, and <strong>Accessories</strong> categories offer a wide range of items to complement your outfit. <strong>Perfumes</strong> have seen growing interest, with a curated selection of popular scents available. <strong>Jerseys</strong> cater to sports fans, while <strong>Sets</strong> offer coordinated outfit bundles at attractive prices. The <strong>Underwear/Underpants</strong> and <strong>Other Stuff</strong> categories complete the catalog with practical everyday items and miscellaneous finds.
            </p>
            <h2>How to Use Category Filters</h2>
            <p>
              When browsing the AllChinaBuy spreadsheet, use the category filter to narrow down your search. Each category is color-coded and clearly labeled, making it easy to jump between sections. Click any category name below to be taken directly to the full product listing on our main website, where you can see detailed images, sizing information, and additional product specifications.
            </p>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Browse All Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group bg-background-50 rounded-xl p-6 border border-background-200/60 hover:border-primary-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-apps-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-foreground-500 text-sm mb-4">
                  Explore {cat.name.toLowerCase()} products in the AllChinaBuy spreadsheet. Browse the full collection on our main website.
                </p>
                <span className="inline-flex items-center text-primary-600 text-sm font-medium">
                  View Products <i className="ri-arrow-right-up-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Want to See All Products at Once?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Visit our Products page to browse all 104 items across every category in a single grid view, or jump straight to the main website for the full spreadsheet experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`/${lang}/products/`}
                className="btn-primary inline-flex items-center justify-center"
              >
                View All Products
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="btn-secondary inline-flex items-center justify-center"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}